import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react"
import Cookie from "js-cookie"
import {supportedLanguages} from "../locales/languages"

const AppContext = createContext(null)

function resolveInitialLanguage() {
  const cookieLang = Cookie.get("selectedLanguage")
  if (cookieLang && supportedLanguages.includes(cookieLang)) {
    return cookieLang
  }
  const domain = typeof document !== "undefined" ? document.domain : ""
  if (domain.includes(".se")) return "se"
  return "en"
}

function resolveInitialHostname() {
  const domain = typeof document !== "undefined" ? document.domain : ""
  if (domain === "localhost") return "toppowernordic.com"
  return domain
}

export function AppProvider({children}) {
  const [language, setLanguageState] = useState(resolveInitialLanguage)
  const [hostname, setHostname] = useState(resolveInitialHostname)
  const [menuIsOpen, setMenuIsOpen] = useState(false)
  const [activeModal, setActiveModal] = useState(null)

  const setLanguage = useCallback((next) => {
    if (!next) return
    if (!supportedLanguages.includes(next)) return
    Cookie.set("selectedLanguage", next)
    setLanguageState(next)
  }, [])

  const openModal = useCallback((name) => setActiveModal(name), [])
  const closeModal = useCallback(() => setActiveModal(null), [])

  useEffect(() => {
    if (!Cookie.get("selectedLanguage")) {
      Cookie.set("selectedLanguage", language)
    }
  }, [language])

  const value = useMemo(() => ({
    language,
    setLanguage,
    hostname,
    setHostname,
    menuIsOpen,
    setMenuIsOpen,
    activeModal,
    openModal,
    closeModal,
  }), [language, setLanguage, hostname, menuIsOpen, activeModal, openModal, closeModal])

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

function useApp() {
  const ctx = useContext(AppContext)
  if (!ctx) throw new Error("AppContext used outside AppProvider")
  return ctx
}

export function useLocale() {
  const {language, setLanguage} = useApp()
  return {language, setLanguage}
}

export function useMenu() {
  const {menuIsOpen, setMenuIsOpen} = useApp()
  return {menuIsOpen, setMenuIsOpen}
}

export function useModal() {
  const {activeModal, openModal, closeModal} = useApp()
  return {activeModal, openModal, closeModal}
}

export function useHostname() {
  const {hostname, setHostname} = useApp()
  return {hostname, setHostname}
}
